import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Mail, Code, Monitor, Cpu, Sun, Moon } from 'lucide-react';
import emailjs from '@emailjs/browser';

// Lignes pour l'animation typewriter → déplacées hors du composant pour la stabilité
const LINES = [
  '> Initialisation du serveur...',
  '> Chargement du framework React...',
  '> Compilation réussie ✅',
  '> Déploiement sur le port 3000 🚀',
];

export default function DevPortfolioEtech() {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });

  const nav = [
    { id: 'accueil', label: 'Accueil' },
    { id: 'services', label: 'Services' },
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'a-propos', label: 'À propos' },
    { id: 'contact', label: 'Contact' },
  ];

  const services = [
    { title: 'Développement Web', desc: "SPA & SSR (React / Next.js). Architecture propre, tests et perf.", icon: <Code size={20} /> },
    { title: 'UI / UX e-tech', desc: 'Design axé tech: néons discrets, typographie monospace, composants modulaires.', icon: <Monitor size={20} /> },
    { title: 'Performances & SEO', desc: "Optimisation Lighthouse, accessibilité et bonnes pratiques SEO.", icon: <Cpu size={20} /> },
  ];

  const projects = [
    { title: 'Plomberie', tech: 'HTML • CSS • JS', img: null },
    { title: 'Site vitrine Topographix', tech: 'HTML • CSS • JS', img: null },
  ];

  const packs = [
    {
      title: "Pack Basique",
      icon: "🏷️",
      desc: "Un site vitrine simple et responsive, parfait pour présenter votre activité.",
      benefits: [
        "Jusqu’à 3 pages (Accueil, Services, Contact)",
        "Adapté à tous les écrans (mobile, tablette, PC)",
        "Installation sur votre hébergement",
      ],
      price: "À partir de 300€",
    },
    {
      title: "Pack Pro",
      icon: "🚀",
      desc: "Un site plus complet, optimisé pour le référencement et avec un formulaire de contact.",
      benefits: [
        "Pack Basique",
        "SEO de base (titres, balises, structure)",
        "Formulaire de contact relié à votre email",
        "Pages supplémentaires (À propos, Réalisations, Mentions légales)",
      ],
      price: "À partir de 500 €",
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const move = (e) => setCursor({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', move);
    return () => window.removeEventListener('mousemove', move);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  function handleContact(e) {
    e.preventDefault();

    emailjs.sendForm(
      'service_n0rcc44',   // Remplace par ton service EmailJS
      'template_1om5qe6',  // Remplace par ton template EmailJS
      e.target,             // le formulaire HTML
      'F4m4jZmlzbZvU1EWA'  // ta clé publique EmailJS
    )
      .then(() => {
        alert('Message envoyé ✅ — merci !');
        e.target.reset();
      })
      .catch(() => {
        alert('Erreur ❌ : message non envoyé.');
      });
  }

  function Typewriter() {
    const [text, setText] = useState('');

    useEffect(() => {
      let currentLine = 0;
      let charIndex = 0;
      let displayText = '';
      const interval = setInterval(() => {
        if (currentLine < LINES.length) {
          if (charIndex < LINES[currentLine].length) {
            displayText += LINES[currentLine][charIndex];
            charIndex++;
          } else {
            displayText += '\n';
            currentLine++;
            charIndex = 0;
          }
          setText(displayText);
        } else {
          clearInterval(interval);
        }
      }, 50);
      return () => clearInterval(interval);
    }, []); // plus de problème d'ESLint ici

    return <pre>{text}</pre>;
  }

  if (loading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-black text-cyan-400 font-mono text-sm">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="mb-3">
          <span className="text-lg">[ Booting Portfolio v2.1 ]</span>
        </motion.div>

        <motion.pre
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ repeat: Infinity, repeatDelay: 0.8 }}
          className="whitespace-pre-wrap text-left"
        >
{`> Initialisation modules UI_React...
> Chargement des assets...
> Démarrage interface futuriste...`}
        </motion.pre>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gradient-to-b from-gray-900 via-slate-900 to-black text-slate-100' : 'bg-gradient-to-b from-slate-100 via-white to-slate-200 text-slate-800'} antialiased transition-colors duration-500`}>
      {/* Fond dynamique */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute w-[120vw] h-[120vh] bg-[radial-gradient(circle_at_center,rgba(0,255,255,0.07)_0%,transparent_70%)] animate-pulse" />
        <div className="absolute inset-0" style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')", opacity: 0.08 }} />
      </div>

      {/* Curseur personnalisé */}
      <motion.div
        className="fixed z-50 w-6 h-6 rounded-full pointer-events-none mix-blend-difference"
        animate={{ x: cursor.x - 12, y: cursor.y - 12, backgroundColor: theme === 'dark' ? '#00FFFF' : '#7C3AED' }}
        transition={{ type: 'spring', mass: 0.3, stiffness: 120, damping: 20 }}
      />

      {/* NAVBAR */}
      <header className="fixed w-full z-40">
        <nav className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="#accueil" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-400 flex items-center justify-center shadow-lg">
              <span className="font-mono font-semibold">DEV</span>
            </div>
            <span className="font-semibold tracking-wide">Sabi Sofia — Développeuse Web Freelance</span>
          </a>

          <div className="hidden md:flex items-center gap-6">
            {nav.map((n) => (
              <a key={n.id} href={`#${n.id}`} className="text-sm hover:text-cyan-400 transition-colors">{n.label}</a>
            ))}

            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-2 rounded-full bg-white/10 hover:bg-white/20">
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <a href="#contact" className="ml-2 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-gradient-to-r from-cyan-500 to-indigo-600 text-black font-medium">Contact</a>
          </div>

          <button className="md:hidden p-2" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </nav>

        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="md:hidden bg-black/60 backdrop-blur-sm">
            <div className="px-6 py-6 flex flex-col gap-4">
              {nav.map((n) => (
                <a key={n.id} href={`#${n.id}`} onClick={() => setOpen(false)} className="block text-lg font-medium">{n.label}</a>
              ))}

              <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="flex items-center gap-2 mt-4">
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />} {theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
              </button>
            </div>
          </motion.div>
        )}
      </header>

      {/* Contenu principal du site */}
      <main className="pt-24">
        {/* HERO */}
        <section id="accueil" className="max-w-6xl mx-auto px-6 py-20 flex flex-col md:flex-row items-center gap-10">
          <motion.div initial={{ x: -40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.6 }} className="flex-1">
            <p className="text-sm uppercase tracking-widest text-cyan-400">Développeuse Web — UI/UX e-tech</p>
            <h1 className="mt-4 text-4xl md:text-5xl font-extrabold leading-tight">Création d'interfaces modernes et performantes</h1>
            <p className="mt-4 text-slate-300 max-w-xl">Expériences rapides, évolutives et esthétiques pour vos projets web.</p>

            <div className="mt-6 flex gap-3">
              <a href="#portfolio" className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-700">Voir mes projets</a>
              <a href="#contact" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-cyan-500 text-black">Me contacter</a>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3 text-xs text-slate-400">
              <div className="p-3 bg-white/3 rounded-md">Langages: HTML • CSS • JS • Dart</div>
              <div className="p-3 bg-white/3 rounded-md">Frameworks: React • Next.js • Flutter</div>
              <div className="p-3 bg-white/3 rounded-md">Outils: Git • Tailwind • HTML5 • CSS3</div>
            </div>
          </motion.div>

          <motion.figure
  initial={{ scale: 0.95, opacity: 0 }}
  animate={{ scale: 1, opacity: 1 }}
  transition={{ duration: 0.6 }}
  className="flex-1 w-full max-w-md bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 shadow-2xl"
>
  <div className="w-full h-56 rounded-md border border-slate-700 bg-black p-4 overflow-hidden font-mono text-sm text-cyan-400">
    <Typewriter />
  </div>

  <div className="mt-6 flex items-center justify-between text-sm text-slate-400">
    <div>
      Temps moyen de livraison:{' '}
      <strong className="text-slate-200">2–4 semaines</strong>
    </div>
    <div className="flex items-center gap-2">
      <Mail size={14} /> sabisofiadu84@gmail.com
    </div>
  </div>
</motion.figure>

        </section>

        {/* SERVICES */}
        <section id="services" className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="text-2xl font-bold">Services</h2>
          <p className="mt-2 text-slate-400">Offres modulables selon besoin — audit, conception, développement et maintenance.</p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((s) => (
              <motion.article key={s.title} whileHover={{ y: -6 }} className="p-6 bg-white/3 rounded-xl border border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-md bg-white/5">{s.icon}</div>
                  <div>
                    <h3 className="font-semibold">{s.title}</h3>
                    <p className="text-sm text-slate-400 mt-1">{s.desc}</p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </section>



    {/* PACKS FREELANCE */}
<section id="packs" className="max-w-6xl mx-auto px-6 py-16">
  <h2 className="text-2xl font-bold">Offres Freelance</h2>
  <p className="mt-2 text-slate-400">
    Offre de lancement exclusive — sur le marché, vous ne trouverez pas un site vitrine à ce prix !
  </p>

  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
    {packs.map((p) => (
      <motion.article
        key={p.title}
        whileHover={{ y: -6 }}
        className="p-6 bg-white/5 rounded-xl border border-slate-700 hover:border-slate-500 transition"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-md bg-white/10 text-lg">{p.icon}</div>
          <h3 className="font-semibold text-lg text-slate-100">{p.title}</h3>
        </div>

        <p className="text-sm text-slate-400 mb-3">{p.desc}</p>
        <p className="text-lg font-semibold text-slate-200 mb-3">{p.price}</p>

        <ul className="list-disc list-inside text-slate-400 text-sm space-y-1">
          {p.benefits.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      </motion.article>
    ))}
  </div>
</section>











        {/* PORTFOLIO */}
<section id="portfolio" className="max-w-6xl mx-auto px-6 py-16">
  <h2 className="text-2xl font-bold">Projets récents</h2>
  <p className="mt-2 text-slate-400">Sites vitrines Profesionnels</p>

  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
    {projects.map((p, i) => (
      <motion.div
        key={p.title}
        whileHover={{ scale: 1.02 }}
        className="p-4 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-slate-700"
      >
        <div className="h-40 rounded-md overflow-hidden border border-slate-700">
          {i === 0 ? (
            <img
              src="/images/plomberie.png"
              alt="Plomberie"
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            />
          ) : (
            <img
              src="/images/topo.png"
              alt="Topographix"
              className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            />
          )}
        </div>
        <div className="mt-4">
          <h3 className="font-semibold">{p.title}</h3>
          <p className="text-sm text-slate-400 mt-1">{p.tech}</p>
          <div className="mt-3 flex gap-2">
            {i === 0 && (
              <a
                href="/sites/plomberie/index.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-3 py-1 border rounded-md"
              >
                Voir le site
              </a>
            )}
            {i === 1 && (
              <a
                href="/sites/Topographix/index.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-3 py-1 border rounded-md"
              >
                Voir le site
              </a>
            )}
          </div>
        </div>
      </motion.div>
    ))}
  </div>
</section>




        {/* A PROPOS */}
        <section id="a-propos" className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-2xl font-bold">À propos</h2>
          <p className="mt-4 text-slate-300">J’aide les entreprises et les porteurs de projet à créer des sites web modernes, performants et faciles à utiliser, qui mettent en valeur leur activité et séduisent leurs visiteurs. Chaque interface est pensée pour être claire, rapide et évolutive, afin de transformer vos visiteurs en clients satisfaits.</p>

          <ul className="mt-6 grid grid-cols-2 gap-3 text-sm text-slate-400">
            <li><strong>Disponibilité :</strong> Freelance / Contrat</li>
            <li><strong>Localisation :</strong> Avignon (remote & on-site)</li>
            <li><strong>Télétravail :</strong> Oui — disponibilités flexibles</li>
            <li><strong>Langues : </strong>Français / Anglais</li>
          </ul>
        </section>

        {/* CONTACT */}
        <section id="contact" className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-2xl font-bold">Contact</h2>
          <p className="mt-2 text-slate-400">Parlons de votre projet — décrivez le besoin, budget et délai souhaité.</p>

          <form onSubmit={handleContact} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <input name="name" required placeholder="Votre nom" className="p-3 rounded-md bg-white/5 border border-slate-700" />
            <input name="email" type="email" required placeholder="Email" className="p-3 rounded-md bg-white/5 border border-slate-700" />
            <input name="phone" placeholder="Téléphone (optionnel)" className="p-3 rounded-md bg-white/5 border border-slate-700" />
            <input name="budget" placeholder="Budget estimé" className="p-3 rounded-md bg-white/5 border border-slate-700" />

            <textarea name="message" required placeholder="Décrivez votre projet" className="md:col-span-2 p-3 rounded-md bg-white/5 border border-slate-700 min-h-[120px]"></textarea>

            <div className="md:col-span-2 flex items-center gap-3">
              <button type="submit" className="px-5 py-2 rounded-md bg-cyan-500 text-black font-medium">Envoyer</button>
              <a href="sabisofiadu84@gmail.com" className="px-4 py-2 rounded-md border">Ou envoyer un email </a>
            </div>
          </form>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-400">
            <div>
              <h4 className="font-semibold">Email</h4>
              <p>sabisofiadu84@gmail.com</p>
            </div>
            <div>
              <h4 className="font-semibold">Téléphone</h4>
              <p>+33 7 69 98 62 16</p>
            </div>
            <div>

  <h4 className="font-semibold">Réseaux</h4>

  <p>

    <a href="https://github.com/sofiasabi84/" target="_blank" rel="noopener noreferrer" className="hover:underline">

      GitHub

    </a> •

    <a href="https://www.linkedin.com/in/sofia-sabi-74b411205/" target="_blank" rel="noopener noreferrer" className="hover:underline"> 

      LinkedIn

    </a> •

    <a href="https://www.instagram.com/sofiasb84/?next=%2Fsofiasb84%2F" target="_blank" rel="noopener noreferrer" className="hover:underline"> 

      Instagram

    </a>

  </p>

</div>


          </div>
        </section>
      </main>

      <footer className="mt-12 border-t border-slate-800 py-6">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-400">
          <div>© {new Date().getFullYear()} Sabi Sofia — Développeuse Web</div>
          <div>Site construit avec ❤️ — Design e‑tech responsive</div>
        </div>
      </footer>
    </div>
  );
}
